import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowRightOutlined, CheckCircleFilled } from "@ant-design/icons";
import { App, Button, theme } from "antd";
import { observer } from "mobx-react";
import { useCallback, useEffect, useState } from "react";
import { ECashbackStatus } from "@devowl-wp/api-real-commerce";
import { LicenseSteps } from "./LicenseSteps.js";
import { SupportSection } from "./SupportSection.js";
import { useStores } from "../../../../store/stores.js";
import { __, _i } from "../../../../utils/i18n.js";
import { getLegalManufacturerUrls } from "../../../../utils/legalEntities.js";
import { MATOMO_MASK } from "../../../../utils/matomoMask.js";
import { disableFomoCoupon } from "../../../fomoCoupon/banner.js";
import { markdownBold } from "../../utils/markdownBold.js";
import { templateReplace } from "../../utils/templateReplace.js";
const externalLinkProps = {
    target: "_blank",
    rel: "noopener noreferrer"
};
/**
 * Post-purchase view. Rendered once `view === "success"` (i.e.
 * `applyPayment` resolved) — `transaction.checkout` is guaranteed here.
 * `PaymentPendingConfirmation` stays on the checkout view because that
 * state has no license/first-name data yet.
 */ const SuccessView = observer(()=>{
    const { checkoutV2Store } = useStores();
    const { product, transaction, customerCenterUrl } = checkoutV2Store;
    const { modal: antdModal } = App.useApp();
    const { token } = theme.useToken();
    const productId = product?.data.productPlan.product.id;
    useEffect(()=>{
        if (typeof productId === "number") {
            disableFomoCoupon({
                reason: "user-of-product",
                productId
            });
        }
        // Strip query params so a refresh doesn't re-run the bootstrap flow.
        window.history.replaceState({}, "", window.location.pathname);
        // Jump back to the top of the page — the buyer often enters the
        // success view scrolled down from where the Paddle iframe sat, and
        // the post-sale state tells a fresh story (hero, steps, license) that
        // should be read from the top.
        window.scrollTo({
            top: 0,
            left: 0,
            behavior: "auto"
        });
    }, [
        productId
    ]);
    // Tracks whether the buyer put the key on the clipboard (via the Copy
    // button or click-to-copy input). We use it below to decide whether the
    // Continue-shopping link needs a "are you sure?" confirm step.
    const [hasCopied, setHasCopied] = useState(false);
    const handleLicenseCopied = useCallback(()=>setHasCopied(true), []);
    const manufacturerHomepage = getLegalManufacturerUrls().link;
    // Service-type purchases (e.g. Cookie Experts) have no licenses, so a
    // "Leave without copying your license key?" confirm would be nonsense
    // — nothing to copy in the first place. Fall through to a direct
    // navigation in that case.
    const hasLicenses = (transaction?.checkout?.licenses?.length ?? 0) > 0;
    const handleContinueShopping = useCallback(()=>{
        if (hasCopied || !hasLicenses) {
            window.location.href = manufacturerHomepage;
            return;
        }
        // Not copied yet — nudge the buyer with a reassuring note that the
        // key and download stay accessible in the customer center. The
        // content link is plain HTML because Antd's Modal.confirm renders
        // React nodes directly (no `_i` interpolation wrapper needed).
        antdModal.confirm({
            title: __("Leave without copying your license key?"),
            content: customerCenterUrl ? /*#__PURE__*/ _jsx("span", {
                children: _i(__("You can re-download %s and find your license key anytime in the {{a}}customer center{{/a}}.", product?.data.productPlan.product.name ?? ""), {
                    a: /*#__PURE__*/ _jsx("a", {
                        href: customerCenterUrl,
                        ...externalLinkProps
                    })
                })
            }) : __("You can re-download and find your license key anytime in the customer center."),
            okText: __("Leave anyway"),
            cancelText: __("Stay here"),
            onOk: ()=>{
                window.location.href = manufacturerHomepage;
            }
        });
    }, [
        hasCopied,
        hasLicenses,
        manufacturerHomepage,
        customerCenterUrl,
        product,
        antdModal
    ]);
    if (!product || !transaction?.checkout) {
        return null;
    }
    const { data: { productPlan: { product: { name, logoSquare, logoFull, headlineValuePropositionAfterSale } } }, websiteCount, manufacturer } = product;
    const { checkout: { licenses, cashbackPromotion, cashbackStatus, subscription: { user: { firstName } } } } = transaction;
    // Cashback follow-up card — celebratory on `Assigned`, low-key on
    // `NotAssigned`. Sits below the installation steps so it doesn't
    // compete with the activation flow.
    let cashbackCard = null;
    if (cashbackPromotion && cashbackStatus === ECashbackStatus.Assigned) {
        const heading = _i(__("You won {{strong}}%d%% cashback{{/strong}} at the %s promotion!", cashbackPromotion.amount, cashbackPromotion.name), {
            strong: /*#__PURE__*/ _jsx("strong", {})
        });
        const body = _i(__("Your order is one of the lucky ones. We've emailed you the claim link - {{strong}}request the payout within 7 days{{/strong}} to receive your cashback on your original payment method."), {
            strong: /*#__PURE__*/ _jsx("strong", {})
        });
        cashbackCard = /*#__PURE__*/ _jsxs("div", {
            className: "checkout-v2-success__cashback checkout-v2-success__cashback--assigned",
            style: {
                background: token.colorBgContainer
            },
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "checkout-v2-success__cashback-title",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            children: "\uD83D\uDCB0 "
                        }),
                        heading
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "checkout-v2-success__cashback-body",
                    children: body
                })
            ]
        });
    } else if (cashbackPromotion && cashbackStatus === ECashbackStatus.NotAssigned) {
        const heading = __("Not your lucky day this time");
        const body = _i(__("Every %d. order wins %d%% back in the {{strong}}%s{{/strong}} promotion - yours wasn't the one this round. Luck is like a wave: it comes and goes, and the next one is already on its way to you.", cashbackPromotion.countOfPurchaseGetAssigned, cashbackPromotion.amount, cashbackPromotion.name), {
            strong: /*#__PURE__*/ _jsx("strong", {})
        });
        cashbackCard = /*#__PURE__*/ _jsxs("div", {
            className: "checkout-v2-success__cashback checkout-v2-success__cashback--not-assigned",
            style: {
                background: token.colorBgLayout
            },
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "checkout-v2-success__cashback-title",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            children: "\uD83C\uDF40 "
                        }),
                        heading
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "checkout-v2-success__cashback-body",
                    children: [
                        body,
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": true,
                            children: " \uD83C\uDF0A✨"
                        })
                    ]
                })
            ]
        });
    }
    const logoUrl = logoFull?.downloadUrl ?? logoSquare?.downloadUrl;
    const headlineNode = headlineValuePropositionAfterSale ? markdownBold(templateReplace(headlineValuePropositionAfterSale, {
        firstName
    })) : null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "checkout-v2-success",
        children: [
            logoUrl && /*#__PURE__*/ _jsx("div", {
                className: "checkout-v2-success__logo",
                children: /*#__PURE__*/ _jsx("img", {
                    src: logoUrl,
                    alt: name
                })
            }),
            headlineNode && /*#__PURE__*/ _jsx("div", {
                className: "checkout-v2-success__headline",
                ...MATOMO_MASK,
                children: headlineNode
            }),
            licenses.length > 0 ? licenses.map((license)=>/*#__PURE__*/ _jsx(LicenseSteps, {
                    license: license,
                    productName: name,
                    websiteCount: websiteCount,
                    customerCenterUrl: customerCenterUrl,
                    onLicenseCopied: handleLicenseCopied
                }, license.id)) : // Service-type purchase (no licenses to hand over). The same
            // card shell as the License Steps card keeps the two sale
            // flavours visually consistent — only the content inside
            // changes. Antd `CheckCircleFilled` in `colorSuccess` sits
            // at the top as the "your order is in" anchor; the existing
            // i18n string below stays intact so translations don't have
            // to be redone.
            /*#__PURE__*/ _jsxs("div", {
                className: "checkout-v2-success__service-card",
                style: {
                    background: token.colorBgContainer
                },
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__service-card__icon",
                        children: /*#__PURE__*/ _jsx(CheckCircleFilled, {
                            style: {
                                color: token.colorSuccess,
                                fontSize: 56
                            }
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "checkout-v2-success__service-card__body",
                        children: _i(__("You will receive an email with further details shortly. If you have any questions, you can always {{a}}open a support ticket{{/a}}."), {
                            a: /*#__PURE__*/ _jsx("a", {
                                href: manufacturer.support,
                                ...externalLinkProps
                            })
                        })
                    })
                ]
            }),
            cashbackCard,
            licenses.length > 0 && /*#__PURE__*/ _jsx(SupportSection, {
                productName: name
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "checkout-v2-success__back",
                children: /*#__PURE__*/ _jsxs(Button, {
                    type: "link",
                    size: "small",
                    onClick: handleContinueShopping,
                    children: [
                        __("Discover more plugins"),
                        " ",
                        /*#__PURE__*/ _jsx(ArrowRightOutlined, {})
                    ]
                })
            })
        ]
    });
});
export { SuccessView };
